/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  accessAPI,
  Boton,
  Titulo,
  CampoInformacion,
  Formulario,
  useFuncionalidad, tituloBase } from "../../../../shared";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Sidebar } from "../../../../shared";

export default function Esquema() {
  const [esquema, setEsquema] = useState(null);
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const conUrsea = useFuncionalidad("URSEA");
  const { esquemaId } = useParams();
  const toast = useRef(null);

  useEffect(() => {
    cargarEsquema();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cargarEsquema() {
    accessAPI(
      "GET",
      `admin/config/esquema/${esquemaId}`,
      null,
      (respuesta) => {
        setEsquema(respuesta);
        document.title = respuesta.etiqueta || respuesta.nombre;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar el esquema",
          sticky: true
        });
      }
    );
  }

  function editarEsquemaSubmit(respuestasFormulario) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/config/esquema/${esquemaId}`,
      respuestasFormulario,
      (respuesta) => {
        setEsquema((prev) => ({ ...prev, ...respuesta }));
        document.title = respuesta.etiqueta || respuesta.nombre;
        setSidebarEditar(false);
        setLoaderEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Esquema actualizado exitosamente"
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar el esquema",
          sticky: true
        });
        setLoaderEditar(false);
      }
    );
  }

  const editarFormConfig = esquema
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "etiqueta",
                tipoitemformularioid: 1,
                etiqueta: "Etiqueta",
                requerido: true,
                respuesta: esquema.etiqueta
              },
              // El nombre en URSEA solo se edita si el organismo tiene la
              // integración prendida.
              ...(conUrsea
                ? [
                    {
                      nombre: "nombre",
                      tipoitemformularioid: 1,
                      etiqueta: "Nombre certificado digital URSEA",
                      respuesta: esquema.nombre
                    }
                  ]
                : []),
              {
                nombre: "descripcion",
                tipoitemformularioid: 1,
                etiqueta: "Descripción",
                requerido: true,
                respuesta: esquema.descripcion
              },
              {
                nombre: "detalles",
                tipoitemformularioid: 11,
                etiqueta: "Detalles",
                respuesta: esquema.detalles
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: editarEsquemaSubmit,
            validar: true
          }
        ]
      }
    : null;

  return (
    <>
      <Toast ref={toast} position="top-center" />

      {!esquema && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          {/* Título skeleton */}
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
              <Skeleton width="2rem" height="2rem" shape="circle" />
              <Skeleton width="15rem" height="2rem" />
            </div>
          </div>

          {/* Card detalles skeleton */}
          <Card>
            <Skeleton
              width="6rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <div className="grid">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="col-12 md:col-4">
                  <Skeleton
                    width="50%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {esquema && (
        <>
          <Titulo urlVolver="/esquemas">
            {esquema.etiqueta || esquema.nombre}
          </Titulo>

          <Card>
            <Titulo
              botones={
                <Boton icono negro callback={() => setSidebarEditar(true)}>
                  <i className="pi pi-pencil" />
                </Boton>
              }
            >
              Detalles
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Etiqueta">
                {esquema.etiqueta || "-"}
              </CampoInformacion>
              {conUrsea && (
                <CampoInformacion nombreCampo="Nombre certificado digital URSEA">
                  {esquema.nombre || "-"}
                </CampoInformacion>
              )}
              <CampoInformacion nombreCampo="Descripción">
                {esquema.descripcion}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Detalles" anchoCompleto>
                {esquema.detalles || "-"}
              </CampoInformacion>
            </div>
          </Card>

          <Sidebar
            visible={sidebarEditar}
            position="right"
            onHide={() => setSidebarEditar(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar esquema</Titulo>
            {editarFormConfig && (
              <Formulario
                key={esquema.id + "-" + esquema.etiqueta}
                config={editarFormConfig}
                loader={loaderEditar}
              />
            )}
          </Sidebar>
        </>
      )}
    </>
  );
}
